import { CookieManager } from '../utils/cookie-manager';
import { formatFileSize } from '../utils/device-detection';

/**
 * 檔案大小提示對話框
 */
export class FileSizeDialog {
    private dialog: HTMLElement;
    private continueButton: HTMLElement;
    private cancelButton: HTMLElement;
    private dontRemindCheckbox: HTMLInputElement;
    private onContinue: (() => void) | null = null;
    private onCancel: (() => void) | null = null;

    constructor() {
        this.createDialog();
    }

    private createDialog(): void {
        // 創建對話框容器
        this.dialog = document.createElement('div');
        this.dialog.id = 'file-size-dialog';
        this.dialog.className = 'file-size-dialog';
        this.dialog.innerHTML = `
            <div class="file-size-dialog-overlay"></div>
            <div class="file-size-dialog-content">
                <div class="file-size-dialog-header">
                    <h3>檔案大小提示</h3>
                </div>
                <div class="file-size-dialog-body">
                    <div class="file-size-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M16 13H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M16 17H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M10 9H9H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <p class="file-size-message">
                        即將載入的 3D 模型檔案大小為 <span id="file-size-value">--</span>，
                        在行動裝置上載入可能需要較長時間。
                    </p>
                    <p class="file-size-sub-message">
                        此提示針對所有檔案，勾選後切換到其他模型時便不再提醒。
                    </p>
                    <div class="file-size-options">
                        <label class="file-size-checkbox-label">
                            <input type="checkbox" id="dont-remind-checkbox">
                            <span class="checkmark"></span>
                            不要再提醒我
                        </label>
                    </div>
                </div>
                <div class="file-size-dialog-footer">
                    <button id="file-size-cancel" class="file-size-button file-size-button-cancel">取消</button>
                    <button id="file-size-continue" class="file-size-button file-size-button-continue">繼續</button>
                </div>
            </div>
        `;

        // 獲取元素引用
        this.continueButton = this.dialog.querySelector('#file-size-continue')!;
        this.cancelButton = this.dialog.querySelector('#file-size-cancel')!;
        this.dontRemindCheckbox = this.dialog.querySelector('#dont-remind-checkbox')! as HTMLInputElement;

        // 綁定事件
        this.bindEvents();

        // 添加到頁面
        document.body.appendChild(this.dialog);
    }

    private bindEvents(): void {
        this.continueButton.addEventListener('click', () => {
            this.handleContinue();
        });

        this.cancelButton.addEventListener('click', () => {
            this.handleCancel();
        });

        // 點擊遮罩層關閉對話框
        this.dialog.querySelector('.file-size-dialog-overlay')?.addEventListener('click', () => {
            this.handleCancel();
        });

        // ESC 鍵關閉對話框
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isVisible()) {
                this.handleCancel();
            }
        });
    }

    private handleContinue(): void {
        // 檢查是否勾選了「不要再提醒我」
        if (this.dontRemindCheckbox.checked) {
            CookieManager.setDontRemindAgain(true);
        }

        this.hide();
        this.onContinue?.();
    }

    private handleCancel(): void {
        this.hide();
        this.onCancel?.();
    }

    /**
     * 顯示對話框
     * @param fileSize 檔案大小（位元組）
     * @param onContinue 繼續回調
     * @param onCancel 取消回調
     */
    public show(fileSize: number, onContinue?: () => void, onCancel?: () => void): void {
        this.onContinue = onContinue || null;
        this.onCancel = onCancel || null;

        // 更新檔案大小顯示
        const fileSizeElement = this.dialog.querySelector('#file-size-value');
        if (fileSizeElement) {
            fileSizeElement.textContent = formatFileSize(fileSize);
        }

        // 重置複選框狀態
        this.dontRemindCheckbox.checked = false;

        // 顯示對話框
        this.dialog.style.display = 'flex';
        document.body.style.overflow = 'hidden'; // 防止背景滾動

        // 聚焦到繼續按鈕
        setTimeout(() => {
            this.continueButton.focus();
        }, 100);
    }

    /**
     * 隱藏對話框
     */
    public hide(): void {
        this.dialog.style.display = 'none';
        document.body.style.overflow = ''; // 恢復背景滾動
    }

    /**
     * 檢查對話框是否可見
     */
    public isVisible(): boolean {
        return this.dialog.style.display === 'flex';
    }

    /**
     * 銷毀對話框
     */
    public destroy(): void {
        if (this.dialog.parentNode) {
            this.dialog.parentNode.removeChild(this.dialog);
        }
    }
}
